import { useState } from "react";
import type { FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/dafuocbep053ijub2020";

export default function SubscribeSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    setFormError("");
    setStatus("idle");

    const form = ev.currentTarget;
    const fd = new FormData(form);

    // 蜜罐字段:非空则不提交并给出同样反馈
    const hp = String(fd.get("website_alt") || "").trim();
    if (hp) {
      setStatus("success");
      form.reset();
      setEmail("");
      return;
    }

    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg = String(meta?.message ?? parsed?.message ?? meta?.detail ?? responseText ?? "");
      const ok = res.ok && parsed?.code === "OK" && !serverMsg.toLowerCase().includes("spam");

      if (ok) {
        setStatus("success");
        form.reset();
        setEmail("");
      } else {
        setStatus("error");
        setFormError(serverMsg && !serverMsg.toLowerCase().includes("spam") ? serverMsg : "订阅失败,请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常,请稍后再试。");
    }
  };

  return (
    <section id="subscribe" className="py-14 md:py-20 bg-foreground-950 relative overflow-hidden">
      {/* 装饰背景 */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20dark%20warm%20terracotta%20amber%20ink%20texture%20with%20subtle%20paper%20grain%20and%20soft%20glow%20editorial%20moody%20background%20deep%20cinematic&width=1600&height=700&seq=chigua-sub-bg&orientation=landscape"
          alt="在线吃瓜订阅区暖色背景"
          className="w-full h-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950 via-foreground-950/85 to-foreground-950/60"></div>
      </div>

      <div className="relative mx-auto max-w-7xl px-4 md:px-6">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500/15 border border-accent-400/40 mb-5">
            <i className="ri-mail-send-line text-accent-300" aria-hidden="true"></i>
            <span className="text-xs md:text-sm text-accent-100 tracking-wide">免费深度周报</span>
          </div>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-snug">
            订阅推送,第一时间
            <br />
            跟上每一场瓜的进度
          </h2>
          <p className="mt-4 text-sm md:text-base text-background-200 leading-relaxed">
            每周为你精选一场值得细品的事件始末与深度复盘。不刷屏、不打扰,只在关键节点更新,帮你把来龙去脉看明白。
          </p>

          <form
            id="subscribe-form"
            data-readdy-form="dafuocbep053ijub2020"
            onSubmit={handleSubmit}
            noValidate
            className="mt-8"
          >
            {/* 蜜罐字段 */}
            <div className="hp-guard" aria-hidden="true">
              <label htmlFor="website_alt">网站</label>
              <input
                type="text"
                id="website_alt"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <label htmlFor="subscribe-email" className="sr-only">
                你的邮箱
              </label>
              <div className="relative flex-1">
                <i
                  className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-base"
                  aria-hidden="true"
                ></i>
                <input
                  type="email"
                  id="subscribe-email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="请输入常用邮箱"
                  className="w-full pl-11 pr-4 py-3 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 text-sm border border-background-300 outline-none focus:ring-2 focus:ring-accent-400 transition"
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-400 transition-colors"
              >
                立即订阅
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </button>
            </div>

            {status === "success" && (
              <p className="mt-4 flex items-center gap-2 text-sm text-accent-300">
                <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                订阅成功!欢迎加入在线吃瓜的理性围观者行列。
              </p>
            )}
            {status === "error" && (
              <p className="mt-4 flex items-center gap-2 text-sm text-primary-300">
                <i className="ri-error-warning-line" aria-hidden="true"></i>
                {formError}
              </p>
            )}
          </form>

          <p className="mt-4 text-xs text-background-400">
            隐私说明:我们仅在发布新复盘时向你推送邮件,绝不外泄或滥用邮箱。
          </p>
        </div>
      </div>
    </section>
  );
}